import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, formatRupiah } from "@/lib/api";

const PERIODS = [
  [1, "Hari Ini"],
  [7, "7 Hari"],
  [30, "30 Hari"],
  [90, "90 Hari"],
  [0, "Semua"],
];

const STATUS_COLOR = {
  "Menunggu Pembayaran": "bg-[#E8D9A8]",
  Lunas: "bg-[#B7CDB4]",
  Dikirim: "bg-[#9FB8D9]",
  Selesai: "bg-[#3D5A40]",
  Dibatalkan: "bg-[#D9A8A8]",
};

const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("id-ID", { day: "numeric", month: "short" });

function StatCard({ label, value, sub, testId, accent }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border p-5 ${accent ? "bg-[#1E2B1D] border-[#1E2B1D] text-[#FAF7EE]" : "bg-[#FBF9F5] border-[#E8E2D5] text-[#1E2B1D]"}`}>
      <span className={`text-[10px] uppercase tracking-wider font-semibold ${accent ? "text-[#A8BAA5]" : "text-[#7E8F7A]"}`}>{label}</span>
      <span className="block font-serif-display text-3xl mt-1">{value}</span>
      {sub && <span className={`text-[11px] ${accent ? "text-[#CBD8C9]" : "text-[#6B7C66]"}`}>{sub}</span>}
    </div>
  );
}

export default function ReportsAdmin() {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);

  useEffect(() => {
    api
      .get(`/reports/orders?days=${days}`)
      .then((r) => setReport(r.data))
      .catch(() => toast.error("Gagal memuat laporan"));
  }, [days]);

  const exportCsv = () => {
    const rows = [["Kode", "Tanggal", "Pembeli", "Telepon", "Total", "Status", "Pembayaran"]];
    report.orders.forEach((o) => rows.push([o.order_code, o.created_at, o.buyer_name, o.phone, o.total, o.status, o.payment_status]));
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    a.download = `laporan-pesanan-${days || "semua"}-hari.csv`;
    a.click();
  };

  if (!report) return <p data-testid="reports-loading" className="text-sm text-[#6B7C66]">Memuat laporan...</p>;

  const { summary, by_status, daily, top_products } = report;
  const maxOrders = Math.max(1, ...daily.map((d) => d.orders));

  return (
    <div data-testid="reports-admin">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">Laporan Pesanan</h1>
          <p className="text-xs text-[#6B7C66] mt-1">Rekap pesanan masuk, status, dan pendapatan.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1 bg-[#EFE9DD] p-1 rounded-xl">
            {PERIODS.map(([d, l]) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`reports-period-${d}`}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${days === d ? "bg-[#3D5A40] text-[#FAF7EE]" : "text-[#3D5A40] hover:bg-[#F4EFE6]"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <button onClick={exportCsv} data-testid="reports-export-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#D5CCBC] text-xs font-semibold text-[#3D5A40] hover:bg-[#F4EFE6]">
            <Download className="w-3.5 h-3.5" /> Ekspor CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <StatCard testId="report-total-orders" label="Pesanan Masuk" value={report.total_orders} accent />
        <StatCard testId="report-pending" label="Pending" value={summary.pending} sub="Menunggu pembayaran" />
        <StatCard testId="report-confirmed" label="Konfirmasi" value={summary.confirmed} sub="Lunas & dikirim" />
        <StatCard testId="report-completed" label="Selesai" value={summary.completed} sub="Pesanan tuntas" />
        <StatCard testId="report-cancelled" label="Dibatalkan" value={summary.cancelled} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5">
          <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A] font-semibold">Pendapatan Terkonfirmasi</span>
          <span data-testid="report-revenue-paid" className="block font-serif-display text-2xl text-[#1E2B1D] mt-1">{formatRupiah(report.revenue_paid)}</span>
          <span className="text-[11px] text-[#6B7C66]">Lunas, dikirim & selesai</span>
        </div>
        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5">
          <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A] font-semibold">Potensi Pendapatan Pending</span>
          <span data-testid="report-revenue-pending" className="block font-serif-display text-2xl text-[#1E2B1D] mt-1">{formatRupiah(report.revenue_pending)}</span>
          <span className="text-[11px] text-[#6B7C66]">{report.awaiting_verification} bukti transfer menunggu verifikasi</span>
        </div>
        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5">
          <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A] font-semibold block mb-3">Komposisi Status</span>
          <div className="flex h-3 rounded-full overflow-hidden bg-[#EFE9DD]" data-testid="report-status-bar">
            {Object.entries(by_status).map(([s, n]) =>
              n > 0 ? <div key={s} title={`${s}: ${n}`} style={{ width: `${(n / Math.max(1, report.total_orders)) * 100}%` }} className={STATUS_COLOR[s]} /> : null
            )}
          </div>
          <ul className="mt-3 space-y-1">
            {Object.entries(by_status).map(([s, n]) => (
              <li key={s} className="flex items-center justify-between text-[11px] text-[#5E6D5B]">
                <span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-sm ${STATUS_COLOR[s]}`} />{s}</span>
                <span data-testid={`report-status-${s.replace(/\s/g, "-")}`} className="font-bold text-[#1E2B1D]">{n}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 mb-8">
        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5">
          <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A] font-semibold block mb-4">Pesanan per Hari</span>
          {daily.length === 0 ? (
            <p data-testid="report-daily-empty" className="text-xs text-[#6B7C66]">Belum ada pesanan pada periode ini.</p>
          ) : (
            <div data-testid="report-daily-chart" className="flex items-end gap-1.5 h-40 overflow-x-auto pb-6 relative">
              {daily.map((d) => (
                <div key={d.date} className="flex flex-col items-center min-w-[2rem] flex-1 group relative">
                  <span className="text-[10px] font-bold text-[#1E2B1D] mb-1">{d.orders}</span>
                  <div title={`${fmtDay(d.date)}: ${d.orders} pesanan, ${formatRupiah(d.revenue)}`} style={{ height: `${(d.orders / maxOrders) * 100}px` }} className="w-full max-w-[2.5rem] rounded-t-md bg-[#3D5A40] group-hover:bg-[#2E4530] transition-colors" />
                  <span className="absolute -bottom-5 text-[9px] text-[#7E8F7A] whitespace-nowrap">{fmtDay(d.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-5">
          <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A] font-semibold block mb-3">Produk Terlaris (terkonfirmasi)</span>
          {top_products.length === 0 ? (
            <p data-testid="report-top-empty" className="text-xs text-[#6B7C66]">Belum ada penjualan terkonfirmasi.</p>
          ) : (
            <ol className="space-y-2" data-testid="report-top-products">
              {top_products.map((p, i) => (
                <li key={p.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-[#1E2B1D]"><span className="w-5 h-5 rounded-full bg-[#E5DEC9] text-[10px] font-bold flex items-center justify-center">{i + 1}</span>{p.name}</span>
                  <span className="text-[#5E6D5B]">{p.qty} pcs · <span className="font-bold text-[#1E2B1D]">{formatRupiah(p.revenue)}</span></span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E8E2D5] flex justify-between items-center">
          <span className="text-[10px] uppercase tracking-wider text-[#7E8F7A] font-semibold">Rincian Pesanan ({report.orders.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="report-orders-table">
            <thead className="bg-[#F4EFE6] text-[#5E6D5B]">
              <tr>
                <th className="text-left p-3 font-semibold">Kode</th>
                <th className="text-left p-3 font-semibold">Tanggal</th>
                <th className="text-left p-3 font-semibold">Pembeli</th>
                <th className="text-right p-3 font-semibold">Total</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-left p-3 font-semibold">Pembayaran</th>
              </tr>
            </thead>
            <tbody>
              {report.orders.map((o) => (
                <tr key={o.id} className="border-t border-[#E8E2D5]">
                  <td className="p-3 font-mono font-bold text-[#3D5A40]">{o.order_code}</td>
                  <td className="p-3 text-[#5E6D5B]">{new Date(o.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td className="p-3 text-[#1E2B1D]">{o.buyer_name}</td>
                  <td className="p-3 text-right font-bold text-[#1E2B1D]">{formatRupiah(o.total)}</td>
                  <td className="p-3"><span className={`inline-flex items-center gap-1.5 text-[#1E2B1D]`}><span className={`w-2 h-2 rounded-full ${STATUS_COLOR[o.status]}`} />{o.status}</span></td>
                  <td className="p-3 text-[#5E6D5B] capitalize">{(o.payment_status || "-").replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
